'use client';

/**
 * Dashboard Settings Panel - Customize dashboard layout and widgets
 */

import { useState, useEffect } from 'react';
import {
  type DashboardSettings,
  type WidgetConfig,
  loadDashboardSettings,
  saveDashboardSettings,
  resetDashboardSettings,
  DEFAULT_SETTINGS,
} from '@/lib/command-center/dashboard-settings';

interface DashboardSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsChange: (settings: DashboardSettings) => void;
}

export default function DashboardSettingsPanel({
  isOpen,
  onClose,
  onSettingsChange,
}: DashboardSettingsPanelProps) {
  const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_SETTINGS);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
       
      setSettings(loadDashboardSettings());
      setHasChanges(false);
    }
  }, [isOpen]);

  const handleToggleWidget = (widgetId: string) => {
    setSettings(prev => ({
      ...prev,
      widgets: prev.widgets.map(w =>
        w.id === widgetId ? { ...w, visible: !w.visible } : w
      ),
    }));
    setHasChanges(true);
  };

  const handleMoveWidget = (widgetId: string, direction: 'up' | 'down') => {
    setSettings(prev => {
      const widgets = [...prev.widgets].sort((a, b) => a.order - b.order);
      const index = widgets.findIndex(w => w.id === widgetId);
      if (index === -1) return prev;

      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= widgets.length) return prev;

      // Swap orders
      const temp = widgets[index].order;
      widgets[index].order = widgets[newIndex].order;
      widgets[newIndex].order = temp;

      return { ...prev, widgets };
    });
    setHasChanges(true);
  };

  const handleCompactModeToggle = () => {
    setSettings(prev => ({ ...prev, compactMode: !prev.compactMode }));
    setHasChanges(true);
  };

  const handleRefreshIntervalChange = (interval: number) => {
    setSettings(prev => ({ ...prev, refreshInterval: interval }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveDashboardSettings(settings);
    onSettingsChange(settings);
    setHasChanges(false);
    onClose();
  };

  const handleReset = () => {
    const defaultSettings = resetDashboardSettings();
    setSettings(defaultSettings);
    onSettingsChange(defaultSettings);
    setHasChanges(false);
  };

  if (!isOpen) return null;

  const sortedWidgets = [...settings.widgets].sort((a, b) => a.order - b.order);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-muted">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent-muted rounded-lg">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Dashboard Settings</h2>
              <p className="text-sm text-foreground-tertiary">Customize your view</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-foreground-tertiary hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Widgets Section */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Widgets</h3>
            <p className="text-xs text-foreground-tertiary mb-4">Toggle visibility and drag to reorder</p>
            <div className="space-y-2">
              {sortedWidgets.map((widget, index) => (
                <div
                  key={widget.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    widget.visible
                      ? 'border-border bg-surface/30'
                      : 'border-border-muted bg-transparent opacity-60'
                  }`}
                >
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMoveWidget(widget.id, 'up')}
                      disabled={index === 0}
                      className="p-0.5 text-foreground-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveWidget(widget.id, 'down')}
                      disabled={index === sortedWidgets.length - 1}
                      className="p-0.5 text-foreground-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Widget info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground text-sm">{widget.name}</div>
                    <div className="text-xs text-foreground-tertiary truncate">{widget.description}</div>
                  </div>

                  {/* Toggle */}
                  <button
                    onClick={() => handleToggleWidget(widget.id)}
                    className={`relative w-10 h-6 rounded-full transition-colors ${
                      widget.visible ? 'bg-accent' : 'bg-cream/20'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        widget.visible ? 'left-5' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Display Options */}
          <div className="pt-4 border-t border-border-muted">
            <h3 className="text-sm font-medium text-foreground mb-3">Display Options</h3>

            {/* Compact Mode */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm text-foreground">Compact Mode</div>
                <div className="text-xs text-foreground-tertiary">Reduce spacing between widgets</div>
              </div>
              <button
                onClick={handleCompactModeToggle}
                className={`relative w-10 h-6 rounded-full transition-colors ${
                  settings.compactMode ? 'bg-accent' : 'bg-cream/20'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    settings.compactMode ? 'left-5' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {/* Auto-refresh */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm text-foreground">Auto-refresh</div>
                <div className="text-xs text-foreground-tertiary">Automatically refresh data</div>
              </div>
              <select
                value={settings.refreshInterval}
                onChange={(e) => handleRefreshIntervalChange(Number(e.target.value))}
                className="bg-surface border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:border-accent"
              >
                <option value={0}>Manual</option>
                <option value={30}>30 seconds</option>
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-muted bg-surface/30">
          <button
            onClick={handleReset}
            className="text-sm text-foreground-tertiary hover:text-foreground transition-colors"
          >
            Reset to defaults
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-foreground-secondary hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="px-4 py-2 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors disabled:opacity-50"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
